/**
 * Optimistic rename mutation hooks for the AC-1377 React Query migration
 * (AC-1405 / Phase 3.4).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 — mutation + invalidation contract
 *   - Section 5.3 — mutation hook signatures (optimistic variants)
 *   - Section 8.2 — Phase 3 verification table
 *
 * ## Why this exists (and why it's separate from `useUpdateWorkflow` /
 * `useUpdateStep`)
 *
 * Renaming a workflow or a step is a high-frequency, low-risk mutation that
 * the user expects to feel instant — the inline edit field commits, the
 * label flips, and the user moves on. A pessimistic round-trip
 * (server write → invalidate → refetch → re-render) introduces a brief
 * flicker that the user reads as "did the rename actually work?".
 *
 * The pessimistic counterparts (`useUpdateWorkflow` from AC-1402 and
 * `useUpdateStep` from AC-1403) cover full updates where the server is
 * the source of truth for many fields. This file is the focused
 * optimistic surface for the single-field rename hot path. The underlying
 * API call is the same `apiService.updateWorkflow` /
 * `apiService.updateWorkflowStep`; the difference is the
 * `onMutate` / `onError` / `onSettled` lifecycle that mutates the cache
 * before the server confirms.
 *
 * Consumer migration (WorkflowEditor, WorkflowsPage, the per-node label
 * inputs) lives in AC-1406. This ticket is additive only — both the
 * pessimistic update hooks and these optimistic rename hooks ship side-by-
 * side and consumers pick the right one.
 *
 * ## Optimistic-update lifecycle
 *
 * Standard TanStack Query "snapshot, optimistic, rollback, settle" pattern.
 * See `useUpdateStepPosition.ts` for a full walkthrough of the four
 * lifecycle phases (`cancelQueries` → snapshot → setQueryData → return
 * context → rollback on error → invalidate on settle); the contract is
 * identical here.
 *
 * ## What's optimistic, what's not
 *
 * - `useRenameWorkflow` writes optimistically to `queryKeys.workflow(id)`
 *   merging only `name` (and `description`, if supplied) into the cached
 *   workflow detail. It does NOT touch `queryKeys.workflows()` (the list
 *   key) optimistically — list rows could include any number of denormalized
 *   fields and a partial optimistic write there would be more confusing
 *   than the brief flicker of a list refetch. The list IS invalidated on
 *   `onSettled` so any active list subscriber refetches with the renamed
 *   workflow.
 *
 * - `useRenameStep` writes optimistically to `queryKeys.workflowSteps(id)`
 *   updating only the matched step's `label`. It does NOT touch
 *   `queryKeys.workflowExecution(id)` optimistically — execution rows
 *   render labels but the optimistic-merge across two cache slots is
 *   error-prone and the brief lag for execution to refetch is acceptable.
 *   Execution IS invalidated on `onSettled` (matching the pessimistic
 *   `useUpdateStep` contract from TD section 2.6).
 *
 * ## Why `useRenameWorkflow` does not take id at hook level
 *
 * Symmetric with `useUpdateWorkflow` (AC-1402): the workflow id arrives at
 * `mutate(...)` time so a single hook instance can serve multiple workflow
 * renames (e.g. inline rename buttons on a list page). `useRenameStep`
 * DOES take workflowId at hook level for the same reason
 * `useUpdateStep(workflowId)` does — step renames always target the
 * workflow whose canvas the user is editing, and closing the id in the
 * hook avoids the call site repeating it at every node-label edit.
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';

import apiService from '../../services/api';
import { Workflow, WorkflowStep } from '../../types';
import { queryKeys } from './queryKeys';

// ---------------------------------------------------------------------------
// useRenameWorkflow
// ---------------------------------------------------------------------------

/**
 * Rename-workflow input. The workflowId is supplied at mutate-time so a
 * single hook instance can serve multiple workflow renames. `description`
 * is optional — many rename UIs (inline title edit) only touch `name`.
 */
export type RenameWorkflowVariables = {
  workflowId: string | number;
  name: string;
  description?: string;
};

/**
 * Context returned from `useRenameWorkflow.onMutate` for rollback.
 * `previousWorkflow` may be undefined if the detail key has not been
 * populated yet (e.g. user is renaming from a list-page row before the
 * detail page has been visited) — the rollback path handles undefined.
 */
export type RenameWorkflowContext = {
  previousWorkflow: Workflow | undefined;
};

/**
 * `useRenameWorkflow()` — optimistic mutation for
 * `PUT /api/v1/orchestrations/:id` with a name (+ optional description)
 * payload.
 *
 * @example
 *   const rename = useRenameWorkflow();
 *   const onSubmit = (newName: string) => {
 *     rename.mutate({ workflowId: id, name: newName });
 *   };
 */
export function useRenameWorkflow(): UseMutationResult<
  Workflow,
  Error,
  RenameWorkflowVariables,
  RenameWorkflowContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    Workflow,
    Error,
    RenameWorkflowVariables,
    RenameWorkflowContext
  >({
    // The payload sent to the server is the rename fields ONLY — see
    // module JSDoc for why we don't widen the optimistic update beyond
    // these fields.
    mutationFn: ({ workflowId, name, description }) => {
      const payload: { name: string; description?: string } = { name };
      if (description !== undefined) payload.description = description;
      return apiService.updateWorkflow(workflowId as string, payload);
    },

    onMutate: async ({ workflowId, name, description }) => {
      // Cancel in-flight workflow-detail refetches so they cannot clobber
      // our optimistic write the moment they land.
      await queryClient.cancelQueries({
        queryKey: queryKeys.workflow(workflowId),
      });

      const previousWorkflow = queryClient.getQueryData<Workflow>(
        queryKeys.workflow(workflowId),
      );

      // Merge ONLY the rename fields into the cached detail. We use the
      // functional setter form so we don't accidentally drop any other
      // fields the cache has accumulated.
      queryClient.setQueryData<Workflow | undefined>(
        queryKeys.workflow(workflowId),
        (old) => {
          if (!old) return old;
          const next = { ...old, name };
          if (description !== undefined) next.description = description;
          return next;
        },
      );

      return { previousWorkflow };
    },

    onError: (_err, vars, context) => {
      if (context) {
        queryClient.setQueryData(
          queryKeys.workflow(vars.workflowId),
          context.previousWorkflow,
        );
      }
      toast.error('Failed to rename workflow');
    },

    onSettled: (_data, _err, vars) => {
      // Re-sync detail with server truth. The list is invalidated too
      // because list rows render the workflow name and a renamed entry
      // must show the new name once the list refetches.
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflow(vars.workflowId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
    },
  });
}

// ---------------------------------------------------------------------------
// useRenameStep
// ---------------------------------------------------------------------------

/**
 * Rename-step input. The stepId is the canonical string-form id from the
 * server (`orchestration_step_id`). The label is the new display name.
 */
export type RenameStepVariables = {
  stepId: string | number;
  label: string;
};

/**
 * Context returned from `useRenameStep.onMutate` for rollback.
 * `previousSteps` may be undefined if the steps cache has not been
 * populated yet — the rollback path handles undefined.
 */
export type RenameStepContext = {
  previousSteps: WorkflowStep[] | undefined;
};

/**
 * `useRenameStep(workflowId)` — optimistic mutation for
 * `PUT /api/v1/orchestrations/steps/:stepId` with a label-only payload.
 *
 * @param workflowId The workflow whose steps cache will be optimistically
 *                   updated. Captured in closure for symmetry with
 *                   `useUpdateStep` / `useUpdateStepPosition`.
 *
 * @example
 *   const rename = useRenameStep(workflowId);
 *   const onCommit = (newLabel: string) => {
 *     rename.mutate({ stepId: '42', label: newLabel });
 *   };
 */
export function useRenameStep(
  workflowId: string | number,
): UseMutationResult<
  WorkflowStep,
  Error,
  RenameStepVariables,
  RenameStepContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowStep,
    Error,
    RenameStepVariables,
    RenameStepContext
  >({
    // Server payload is label only — same rationale as the workflow
    // rename mutationFn above.
    mutationFn: ({ stepId, label }) =>
      apiService.updateWorkflowStep(stepId as string, { label }),

    onMutate: async ({ stepId, label }) => {
      // Prevent an in-flight steps refetch from clobbering our write.
      await queryClient.cancelQueries({
        queryKey: queryKeys.workflowSteps(workflowId),
      });

      const previousSteps = queryClient.getQueryData<WorkflowStep[]>(
        queryKeys.workflowSteps(workflowId),
      );

      // Update only the matched step's label. Coerce both sides of the
      // id comparison to string so callers can pass either form.
      queryClient.setQueryData<WorkflowStep[] | undefined>(
        queryKeys.workflowSteps(workflowId),
        (oldSteps) => {
          if (!oldSteps) return oldSteps;
          return oldSteps.map((s) =>
            String(s.orchestration_step_id) === String(stepId)
              ? { ...s, label }
              : s,
          );
        },
      );

      return { previousSteps };
    },

    onError: (_err, _vars, context) => {
      if (context) {
        queryClient.setQueryData(
          queryKeys.workflowSteps(workflowId),
          context.previousSteps,
        );
      }
      toast.error('Failed to rename step');
    },

    onSettled: () => {
      // Re-sync steps with server truth. Execution is invalidated too
      // because execution rows render step labels — matches the
      // `useUpdateStep` contract from TD section 2.6.
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowSteps(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowExecution(workflowId),
      });
    },
  });
}
